import React, { useEffect, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { fetchAllCardRequests, updateCardRequestStatus, updateCardRequest, fetchUsers, updateUser, fetchValentCopies, fetchSubscribers, CardRequest, User, ValentCopyEvent } from "../utils/api";
import Button from "./ui/Button";
import { ChevronDown } from "lucide-react";

type FilterType = "all" | "pending" | "approved" | "rejected";

interface Testimonial {
  id: number;
  quote: string;
  author: string;
  role: string;
  rating: number;
}

const Admin: React.FC = () => {
  const { user } = useUser();
  // D-002: the admin gate keys off the verified Clerk role claim
  // (public_metadata.role === 'admin'), not a client email list. This gate is
  // UX-only — real authorization is enforced server-side.
  const isAdmin = (user?.publicMetadata as { role?: string } | undefined)?.role === 'admin';

  const [requests, setRequests] = useState<CardRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [valentCopies, setValentCopies] = useState<ValentCopyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [activeTab, setActiveTab] = useState<'requests' | 'users' | 'subscribers' | 'testimonials' | 'valent'>('requests');
  const [trackingInput, setTrackingInput] = useState<Record<string, string>>({});
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const [expandedValent, setExpandedValent] = useState<number | null>(null);
  // Testimonials start empty — only real customer quotes get added.
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({ quote: '', author: '', role: '', rating: 5 });

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    loadData();
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const [reqData, userData, subData, valentData] = await Promise.all([
        fetchAllCardRequests().catch(() => []),
        fetchUsers().catch(() => []),
        fetchSubscribers().catch(() => []),
        fetchValentCopies().catch(() => []),
      ]);
      setRequests(reqData || []);
      setUsers(userData || []);
      setSubscribers(subData || []);
      setValentCopies(valentData || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      await updateCardRequestStatus(id, newStatus);
      setRequests(requests.map(r =>
        r.id === id ? { ...r, status: newStatus } : r
      ));
      setStatus({ message: `Request ${newStatus} successfully`, type: "success" });
      setTimeout(() => setStatus(null), 3000);
    } catch (err) {
      setStatus({ message: "Failed to update status", type: "error" });
      setTimeout(() => setStatus(null), 3000);
    }
  };

  const filteredRequests = requests
    .filter(r => filter === "all" || (r.status || "pending") === filter)
    .filter(r => {
      if (!search) return true;
      const s = search.toLowerCase();
      return r.name.toLowerCase().includes(s) || r.email.toLowerCase().includes(s);
    });

  const counts = {
    all: requests.length,
    pending: requests.filter(r => !r.status || r.status === "pending").length,
    approved: requests.filter(r => r.status === "approved").length,
    rejected: requests.filter(r => r.status === "rejected").length,
  };

  const getStatusBadge = (requestStatus: string | undefined) => {
    const s = requestStatus || "pending";
    const styles: Record<string, string> = {
      pending: "bg-amber-50 text-amber-800 border border-amber-200",
      approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      rejected: "bg-red-50 text-red-700 border border-red-200",
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium tracking-eyebrow ${styles[s] || styles.pending}`}>
        {s.charAt(0).toUpperCase() + s.slice(1)}
      </span>
    );
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleAddTestimonial = () => {
    if (!newTestimonial.quote || !newTestimonial.author || !newTestimonial.role) return;
    const id = testimonials.length > 0 ? Math.max(...testimonials.map(t => t.id)) + 1 : 1;
    setTestimonials([...testimonials, { ...newTestimonial, id }]);
    setNewTestimonial({ quote: '', author: '', role: '', rating: 5 });
    setShowTestimonialForm(false);
  };

  const handleUpdateTestimonial = () => {
    if (!editingTestimonial) return;
    setTestimonials(testimonials.map(t => t.id === editingTestimonial.id ? editingTestimonial : t));
    setEditingTestimonial(null);
  };

  const handleDeleteTestimonial = (id: number) => {
    setTestimonials(testimonials.filter(t => t.id !== id));
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? "text-amber-400" : "text-coffee-200"}>&#9733;</span>
    ));
  };

  const filterTabs: { key: FilterType; label: string }[] = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
  ];

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="eyebrow mb-3">Access denied</div>
          <h1 className="text-display text-3xl text-coffee-900 dark:text-cream-50 mb-2">Restricted area</h1>
          <p className="text-coffee-600 dark:text-cream-200">This page is restricted to administrators.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="animate-pulse space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-16 rounded-xl bg-coffee-50 dark:bg-coffee-900" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-coffee-900 dark:text-cream-50">
      {/* Page header */}
      <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="eyebrow mb-1.5">Admin</div>
              <h1 className="text-display text-3xl sm:text-4xl text-coffee-900 dark:text-cream-50">Dashboard</h1>
              <p className="mt-1.5 text-coffee-600 dark:text-cream-200 font-mono text-sm">
                {users.length} users &middot; {requests.length} orders &middot; {subscribers.length} subscribers
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 pt-1">
              <Button variant="secondary" size="sm" onClick={loadData}>Refresh</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky tab bar */}
      <div className="border-b border-coffee-100 dark:border-coffee-800 bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile: dropdown to avoid horizontal overflow on small screens */}
          <div className="sm:hidden py-3">
            <label htmlFor="admin-tab-select" className="sr-only">Select section</label>
            <select
              id="admin-tab-select"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as typeof activeTab)}
              className="w-full max-w-full px-3 py-2.5 text-sm font-medium capitalize rounded-md border border-coffee-200 dark:border-coffee-700 bg-white dark:bg-coffee-900 text-coffee-900 dark:text-cream-50"
            >
              {(['requests', 'users', 'subscribers', 'testimonials', 'valent'] as const).map(tab => (
                <option key={tab} value={tab}>{tab}</option>
              ))}
            </select>
          </div>

          {/* Desktop: horizontal tab row */}
          <div className="hidden sm:flex">
            {(['requests', 'users', 'subscribers', 'testimonials', 'valent'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-3.5 text-sm font-medium transition-colors border-b-2 capitalize ${
                  activeTab === tab
                    ? 'border-coffee-600 text-coffee-900 dark:text-cream-50'
                    : 'border-transparent text-coffee-500 dark:text-cream-300 hover:text-coffee-700 hover:border-coffee-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Users Tab */}
        {activeTab === 'users' && (() => {
          const filteredUsers = users.filter(u => {
            if (!search) return true;
            const s = search.toLowerCase();
            return (u.firstName || '').toLowerCase().includes(s) || (u.lastName || '').toLowerCase().includes(s) || u.email.toLowerCase().includes(s) || (u.username || '').toLowerCase().includes(s);
          });

          const handleTogglePlan = async (u: User) => {
            const newPlan = (u.subscription === 'pro' || u.subscription === 'paid') ? 'free' : 'pro';
            if (!window.confirm(`${newPlan === 'pro' ? 'Upgrade' : 'Downgrade'} ${u.firstName || u.email} to ${newPlan}?`)) return;
            try {
              await updateUser(u.id, { subscription: newPlan } as any);
              setUsers(users.map(usr => usr.id === u.id ? { ...usr, subscription: newPlan } : usr));
              setStatus({ message: `${u.firstName || u.email} ${newPlan === 'pro' ? 'upgraded to Pro' : 'downgraded to Free'}`, type: 'success' });
              setTimeout(() => setStatus(null), 3000);
            } catch { setStatus({ message: 'Failed to update plan', type: 'error' }); setTimeout(() => setStatus(null), 3000); }
          };

          const handleToggleBan = async (u: User) => {
            const isBanned = u.subscription === 'banned';
            const action = isBanned ? 'reinstate' : 'ban';
            if (!window.confirm(`${isBanned ? 'Reinstate' : 'Ban'} ${u.firstName || u.email}?`)) return;
            try {
              await updateUser(u.id, { subscription: isBanned ? 'free' : 'banned' } as any);
              setUsers(users.map(usr => usr.id === u.id ? { ...usr, subscription: isBanned ? 'free' : 'banned' } : usr));
              setStatus({ message: `${u.firstName || u.email} ${isBanned ? 'reinstated' : 'banned'}`, type: 'success' });
              setTimeout(() => setStatus(null), 3000);
            } catch { setStatus({ message: `Failed to ${action}`, type: 'error' }); setTimeout(() => setStatus(null), 3000); }
          };

          const generateQr = (username: string) => {
            const url = `https://whobrew.us/card/${username}?ref=nfc`;
            const qr = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(url)}`;
            window.open(qr, '_blank');
          };

          return (
            <div className="space-y-4">
              <div className="flex gap-3 items-center">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email, or username..."
                  className="flex-1 px-4 py-2.5 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm focus:ring-2 focus:ring-coffee-200 focus:border-coffee-500 text-coffee-900 dark:text-cream-50 transition-colors"
                />
                <span className="text-sm font-mono text-coffee-500 dark:text-cream-300 whitespace-nowrap">{filteredUsers.length} users</span>
              </div>

              {status && (
                <div className={`p-3 rounded-xl text-sm border ${status.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
                  {status.message}
                </div>
              )}

              <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl overflow-hidden shadow-coffee">
                {filteredUsers.length === 0 ? (
                  <div className="p-12 text-center text-coffee-500 dark:text-cream-300 text-sm">No users match your search.</div>
                ) : (
                  <div className="divide-y divide-coffee-100">
                    {filteredUsers.map(u => {
                      const isPro = u.subscription === 'pro' || u.subscription === 'paid';
                      const isBanned = u.subscription === 'banned';
                      return (
                        <div key={u.id} className={`p-4 hover:bg-coffee-50 transition-colors ${isBanned ? 'opacity-60' : ''}`}>
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-9 h-9 bg-coffee-100 dark:bg-coffee-800 rounded-full flex items-center justify-center text-coffee-700 dark:text-cream-200 font-semibold text-sm flex-shrink-0">
                                {(u.firstName?.[0] || u.email[0]).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-coffee-900 dark:text-cream-50 truncate">{u.firstName} {u.lastName}</p>
                                <p className="text-xs font-mono text-coffee-500 dark:text-cream-300 truncate">{u.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {isBanned ? (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium tracking-eyebrow bg-red-50 text-red-700 border border-red-200">Banned</span>
                              ) : (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium tracking-eyebrow ${isPro ? 'bg-amber-50 text-amber-800 border border-amber-200' : 'bg-coffee-50 text-coffee-600 dark:text-cream-200 border border-coffee-200 dark:border-coffee-700'}`}>
                                  {isPro ? 'Pro' : 'Free'}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-xs font-mono text-coffee-400 dark:text-cream-400 mt-2 mb-3">
                            <span>@{u.username}</span>
                            <span>&middot;</span>
                            <span>{new Date(u.createdAt).toLocaleDateString()}</span>
                            {u.hasPhysicalCard && <><span>&middot;</span><span>physical card</span></>}
                          </div>

                          <div className="flex flex-wrap gap-1.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/card/${u.username}`, '_blank')}
                            >
                              View card
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => generateQr(u.username)}
                            >
                              Generate QR
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTogglePlan(u)}
                            >
                              {isPro ? 'Downgrade to Free' : 'Upgrade to Pro'}
                            </Button>
                            <Button
                              variant={isBanned ? "ghost" : "danger"}
                              size="sm"
                              onClick={() => handleToggleBan(u)}
                            >
                              {isBanned ? 'Reinstate' : 'Ban user'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(u.id);
                                setStatus({ message: `User ID copied: ${u.id}`, type: 'success' });
                                setTimeout(() => setStatus(null), 2000);
                              }}
                            >
                              Copy ID
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-mono text-coffee-500 dark:text-cream-300">{subscribers.length} subscribers</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  const csv = 'Email,Source,Date\r\n' + subscribers.map((s: any) =>
                    `${s.email},${s.source || 'landing'},${new Date(s.created_at).toLocaleDateString()}`
                  ).join('\r\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'whobrew-subscribers.csv'; a.click();
                  URL.revokeObjectURL(url);
                }}
              >
                Export CSV
              </Button>
            </div>

            <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl overflow-hidden shadow-coffee">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-coffee-50 dark:bg-coffee-900 border-b border-coffee-100 dark:border-coffee-800">
                      <th className="text-left px-5 py-3 text-[11px] tracking-eyebrow font-semibold text-coffee-500 dark:text-cream-300 uppercase">Email</th>
                      <th className="text-left px-5 py-3 text-[11px] tracking-eyebrow font-semibold text-coffee-500 dark:text-cream-300 uppercase">Source</th>
                      <th className="text-left px-5 py-3 text-[11px] tracking-eyebrow font-semibold text-coffee-500 dark:text-cream-300 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-coffee-100">
                    {subscribers.map((s: any, i: number) => (
                      <tr key={i} className="hover:bg-coffee-50 transition-colors">
                        <td className="px-5 py-3 text-coffee-900 dark:text-cream-50 font-mono text-xs">{s.email}</td>
                        <td className="px-5 py-3 text-coffee-600 dark:text-cream-200 text-xs">{s.source || 'landing'}</td>
                        <td className="px-5 py-3 text-coffee-500 dark:text-cream-300 font-mono text-xs">{new Date(s.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                    {subscribers.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-5 py-10 text-center text-coffee-500 dark:text-cream-300 text-sm">No subscribers yet.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (() => {
          const PIPELINE_STAGES = [
            { key: 'pending', label: 'New Orders', colorCard: 'border-amber-200 bg-amber-50', badge: 'bg-amber-50 text-amber-800 border-amber-200' },
            { key: 'in-progress', label: 'In Production', colorCard: 'border-coffee-200 bg-coffee-50 dark:bg-coffee-900', badge: 'bg-coffee-50 text-coffee-700 dark:text-cream-200 border-coffee-200 dark:border-coffee-700' },
            { key: 'completed', label: 'Ready to Ship', colorCard: 'border-cream-100 bg-cream-50 dark:bg-coffee-900', badge: 'bg-cream-100 text-cream-900 border-cream-100' },
            { key: 'shipped', label: 'Shipped', colorCard: 'border-emerald-200 bg-emerald-50', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
          ];

          const moveToStage = async (reqId: string, newStatus: string, trackingNumber?: string) => {
            try {
              const body: any = { status: newStatus };
              if (trackingNumber) body.trackingNumber = trackingNumber;
              if (newStatus === 'shipped') body.shippedAt = new Date().toISOString();
              await updateCardRequestStatus(reqId, newStatus);
              // Persist tracking/shipping fields too. Routed through apiFetch
              // (D-002) so the request carries the admin's bearer token.
              await updateCardRequest(reqId, body);
              setRequests(requests.map(r => r.id === reqId ? { ...r, status: newStatus, trackingNumber: trackingNumber || r.trackingNumber } : r));
              setStatus({ message: `Order moved to ${newStatus}`, type: 'success' });
              setTimeout(() => setStatus(null), 3000);
            } catch {
              setStatus({ message: 'Failed to update order', type: 'error' });
              setTimeout(() => setStatus(null), 3000);
            }
          };

          const getNextStage = (current: string | undefined) => {
            const idx = PIPELINE_STAGES.findIndex(s => s.key === (current || 'pending'));
            return idx < PIPELINE_STAGES.length - 1 ? PIPELINE_STAGES[idx + 1] : null;
          };

          return (
            <div className="space-y-6">
              {status && (
                <div className={`p-3 rounded-xl text-sm border ${status.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
                  {status.message}
                </div>
              )}

              {/* Pipeline stat tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PIPELINE_STAGES.map(stage => (
                  <div key={stage.key} className={`rounded-xl p-4 border ${stage.colorCard}`}>
                    <p className="text-[11px] tracking-eyebrow font-semibold text-coffee-600 dark:text-cream-200 uppercase">{stage.label}</p>
                    <p className="text-2xl font-mono text-coffee-900 dark:text-cream-50 mt-1">
                      {requests.filter(r => (r.status || 'pending') === stage.key).length}
                    </p>
                  </div>
                ))}
              </div>

              {/* Pipeline by stage */}
              {PIPELINE_STAGES.map(stage => {
                const stageOrders = requests.filter(r => (r.status || 'pending') === stage.key);
                if (stageOrders.length === 0) return null;
                return (
                  <div key={stage.key}>
                    <div className="eyebrow mb-3 flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full inline-block ${stage.badge.split(' ')[0].replace('bg-', 'bg-')}`} />
                      {stage.label} ({stageOrders.length})
                    </div>
                    <div className="space-y-2">
                      {stageOrders.map(order => {
                        const next = getNextStage(order.status);
                        const isExpanded = expandedOrder === order.id;
                        const cardUsername = order.websiteUrl?.match(/\/card\/([^/?]+)/)?.[1] || '';
                        return (
                          <div key={order.id} className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-xl overflow-hidden shadow-coffee hover:shadow-coffee-lg transition-shadow">
                            {/* Order header */}
                            <button
                              onClick={() => setExpandedOrder(isExpanded ? null : order.id!)}
                              className="w-full text-left p-4 hover:bg-coffee-50 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                  <p className="font-medium text-coffee-900 dark:text-cream-50">{order.name}</p>
                                  <p className="text-xs font-mono text-coffee-500 dark:text-cream-300 mt-0.5">{order.email} &middot; {formatDate(order.createdAt)}</p>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {order.trackingNumber && (
                                    <span className="text-xs font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">{order.trackingNumber}</span>
                                  )}
                                  <span className={`text-coffee-400 text-xs transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
                                </div>
                              </div>
                            </button>

                            {/* Expanded details */}
                            {isExpanded && (
                              <div className="border-t border-coffee-100 dark:border-coffee-800 p-4 space-y-4 bg-coffee-50 dark:bg-coffee-900/50">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                  <p><span className="font-medium text-coffee-700 dark:text-cream-200">Phone:</span> <span className="font-mono text-coffee-900 dark:text-cream-50">{order.phoneNumber || 'N/A'}</span></p>
                                  <p><span className="font-medium text-coffee-700 dark:text-cream-200">Position:</span> <span className="text-coffee-900 dark:text-cream-50">{order.position1 || 'N/A'}</span></p>
                                  {order.websiteUrl && (
                                    <p className="sm:col-span-2"><span className="font-medium text-coffee-700 dark:text-cream-200">Card URL:</span>{' '}
                                      <a href={order.websiteUrl} target="_blank" rel="noopener noreferrer" className="font-mono text-coffee-600 dark:text-cream-200 underline hover:text-coffee-800 text-xs break-all">{order.websiteUrl}</a>
                                    </p>
                                  )}
                                </div>

                                {order.comments && (
                                  <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-lg p-3">
                                    <p className="text-[11px] tracking-eyebrow font-semibold text-coffee-500 dark:text-cream-300 uppercase mb-1">Order notes</p>
                                    <p className="text-sm text-coffee-700 dark:text-cream-200 whitespace-pre-wrap">{order.comments}</p>
                                  </div>
                                )}

                                {cardUsername && (
                                  <a
                                    href={`/card/${cardUsername}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-coffee-200 dark:border-coffee-700 text-coffee-700 dark:text-cream-200 rounded-lg text-sm font-medium hover:bg-coffee-50 transition-colors"
                                  >
                                    Preview card: <span className="font-mono">@{cardUsername}</span>
                                  </a>
                                )}

                                {/* Pipeline actions */}
                                <div className="flex flex-wrap gap-2 pt-2 border-t border-coffee-100 dark:border-coffee-800">
                                  {next && stage.key !== 'shipped' && stage.key !== 'completed' && (
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => moveToStage(order.id!, next.key)}
                                    >
                                      Move to {next.label}
                                    </Button>
                                  )}
                                  {stage.key === 'completed' && (
                                    <div className="flex items-center gap-2 w-full">
                                      <input
                                        type="text"
                                        value={trackingInput[order.id!] || ''}
                                        onChange={(e) => setTrackingInput({ ...trackingInput, [order.id!]: e.target.value })}
                                        placeholder="Tracking number (optional)"
                                        className="flex-1 px-3 py-2 border border-coffee-200 dark:border-coffee-700 rounded-lg text-sm font-mono focus:ring-2 focus:ring-coffee-200 focus:border-coffee-500 transition-colors"
                                      />
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => moveToStage(order.id!, 'shipped', trackingInput[order.id!] || undefined)}
                                      >
                                        Mark shipped
                                      </Button>
                                    </div>
                                  )}
                                  {stage.key !== 'pending' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => moveToStage(order.id!, 'pending')}
                                    >
                                      Move back to New Orders
                                    </Button>
                                  )}
                                  {cardUsername && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        const url = `https://whobrew.us/card/${cardUsername}?ref=nfc`;
                                        window.open(`https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${encodeURIComponent(url)}`, '_blank');
                                      }}
                                    >
                                      Generate QR
                                    </Button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {requests.length === 0 && (
                <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl p-12 text-center shadow-coffee">
                  <p className="text-coffee-500 dark:text-cream-300 text-sm">No orders yet.</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                variant="primary"
                size="sm"
                onClick={() => { setShowTestimonialForm(true); setEditingTestimonial(null); }}
              >
                Add testimonial
              </Button>
            </div>

            {/* Add/Edit Form */}
            {(showTestimonialForm || editingTestimonial) && (
              <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl shadow-coffee p-6">
                <div className="eyebrow mb-3">
                  {editingTestimonial ? 'Edit testimonial' : 'New testimonial'}
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-coffee-700 dark:text-cream-200 mb-1.5">Quote</label>
                    <textarea
                      value={editingTestimonial ? editingTestimonial.quote : newTestimonial.quote}
                      onChange={e => editingTestimonial
                        ? setEditingTestimonial({ ...editingTestimonial, quote: e.target.value })
                        : setNewTestimonial({ ...newTestimonial, quote: e.target.value })
                      }
                      className="w-full px-4 py-2.5 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-200 focus:border-coffee-500 text-coffee-900 dark:text-cream-50 transition-colors"
                      rows={3}
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-coffee-700 dark:text-cream-200 mb-1.5">Author name</label>
                      <input
                        type="text"
                        value={editingTestimonial ? editingTestimonial.author : newTestimonial.author}
                        onChange={e => editingTestimonial
                          ? setEditingTestimonial({ ...editingTestimonial, author: e.target.value })
                          : setNewTestimonial({ ...newTestimonial, author: e.target.value })
                        }
                        className="w-full px-4 py-2.5 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-200 focus:border-coffee-500 text-coffee-900 dark:text-cream-50 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-coffee-700 dark:text-cream-200 mb-1.5">Role</label>
                      <input
                        type="text"
                        value={editingTestimonial ? editingTestimonial.role : newTestimonial.role}
                        onChange={e => editingTestimonial
                          ? setEditingTestimonial({ ...editingTestimonial, role: e.target.value })
                          : setNewTestimonial({ ...newTestimonial, role: e.target.value })
                        }
                        className="w-full px-4 py-2.5 border border-coffee-200 dark:border-coffee-700 rounded-lg focus:ring-2 focus:ring-coffee-200 focus:border-coffee-500 text-coffee-900 dark:text-cream-50 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-coffee-700 dark:text-cream-200 mb-1.5">Rating</label>
                    <div className="flex gap-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => editingTestimonial
                            ? setEditingTestimonial({ ...editingTestimonial, rating: i + 1 })
                            : setNewTestimonial({ ...newTestimonial, rating: i + 1 })
                          }
                          className={`text-2xl transition-colors ${
                            i < (editingTestimonial ? editingTestimonial.rating : newTestimonial.rating)
                              ? 'text-amber-400'
                              : 'text-coffee-200'
                          } hover:text-amber-400`}
                        >
                          &#9733;
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={editingTestimonial ? handleUpdateTestimonial : handleAddTestimonial}
                    >
                      {editingTestimonial ? 'Save changes' : 'Add testimonial'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => { setShowTestimonialForm(false); setEditingTestimonial(null); }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Testimonials list */}
            {testimonials.length === 0 ? (
              <div className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-2xl shadow-coffee p-12 text-center">
                <p className="text-coffee-500 dark:text-cream-300 text-sm">No testimonials yet.</p>
                <p className="text-xs text-coffee-400 dark:text-cream-400 mt-1">Only real customer quotes go here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {testimonials.map(testimonial => (
                  <div
                    key={testimonial.id}
                    className="bg-white border border-coffee-100 dark:border-coffee-800 rounded-xl shadow-coffee p-5 hover:shadow-coffee-lg transition-shadow"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-2 text-lg">{renderStars(testimonial.rating)}</div>
                        <p className="text-coffee-700 dark:text-cream-200 italic text-sm mb-3">"{testimonial.quote}"</p>
                        <p className="text-sm font-semibold text-coffee-900 dark:text-cream-50">{testimonial.author}</p>
                        <p className="text-xs text-coffee-500 dark:text-cream-300">{testimonial.role}</p>
                      </div>
                      <div className="flex flex-row lg:flex-col gap-2 flex-shrink-0">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => { setEditingTestimonial(testimonial); setShowTestimonialForm(false); }}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteTestimonial(testimonial.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Valent Tab — email-signature copy analytics from /valent */}
        {activeTab === 'valent' && (() => {
          const total = valentCopies.length;
          const people = new Set(
            valentCopies.map(c => (c.name || c.email || 'anonymous').trim().toLowerCase())
          );
          const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
          const last7 = valentCopies.filter(c => new Date(c.created_at).getTime() >= weekAgo).length;
          const stats = [
            { label: 'Total copies', value: total },
            { label: 'People', value: people.size },
            { label: 'Last 7 days', value: last7 },
          ];
          return (
            <div className="space-y-6">
              {/* auto-fit stat cards: resize / wrap instead of overflowing */}
              <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
                {stats.map(s => (
                  <div key={s.label} className="rounded-2xl border border-coffee-100 dark:border-coffee-800 bg-white dark:bg-coffee-900 p-5">
                    <div className="text-3xl font-semibold text-coffee-900 dark:text-cream-50">{s.value}</div>
                    <div className="eyebrow mt-1 text-coffee-500 dark:text-cream-300">{s.label}</div>
                  </div>
                ))}
              </div>

              {valentCopies.length === 0 ? (
                <p className="text-sm text-coffee-500 dark:text-cream-300">No signature copies yet.</p>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-coffee-100 dark:border-coffee-800">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead className="bg-coffee-50 dark:bg-coffee-900 text-left">
                      <tr>
                        {['Name', 'Email', 'Phone', 'When'].map(h => (
                          <th key={h} className="px-4 py-3 eyebrow font-normal text-coffee-500 dark:text-cream-300">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {valentCopies.map(c => {
                        // `fields` is a JSON map of optional field -> value. Tolerate
                        // the older boolean shape by keeping only string values.
                        let opt: Record<string, string> = {};
                        try {
                          const f = c.fields ? JSON.parse(c.fields) : {};
                          Object.entries(f).forEach(([k, v]) => {
                            if (typeof v === 'string' && v.trim()) opt[k] = v;
                          });
                        } catch {}
                        const optKeys = Object.keys(opt);
                        const hasOpt = optKeys.length > 0;
                        const open = expandedValent === c.id;
                        return (
                          <React.Fragment key={c.id}>
                            <tr className="border-t border-coffee-100 dark:border-coffee-800">
                              <td className="px-4 py-3 text-coffee-900 dark:text-cream-50">
                                {hasOpt ? (
                                  <button
                                    type="button"
                                    onClick={() => setExpandedValent(open ? null : c.id)}
                                    className="inline-flex items-center gap-1.5 text-left hover:text-coffee-600 dark:hover:text-cream-100"
                                    aria-expanded={open}
                                  >
                                    <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
                                    {c.name || '—'}
                                  </button>
                                ) : (
                                  <span className="pl-[1.25rem]">{c.name || '—'}</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-coffee-700 dark:text-cream-200 break-all">{c.email || '—'}</td>
                              <td className="px-4 py-3 text-coffee-700 dark:text-cream-200 whitespace-nowrap">{c.phone || '—'}</td>
                              <td className="px-4 py-3 text-coffee-500 dark:text-cream-300 whitespace-nowrap">{new Date(c.created_at).toLocaleString()}</td>
                            </tr>
                            {open && hasOpt && (
                              <tr className="bg-coffee-50 dark:bg-coffee-900/40">
                                <td colSpan={4} className="px-4 py-3">
                                  <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
                                    {optKeys.map(k => (
                                      <div key={k} className="min-w-0">
                                        <div className="eyebrow text-coffee-400 dark:text-cream-400 capitalize">{k}</div>
                                        <div className="text-sm text-coffee-800 dark:text-cream-100 break-all">{opt[k]}</div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

      </div>
    </div>
  );
};

export default Admin;
